import React from 'react'

import { Formik } from 'formik'
import queryString from 'query-string'
import { useLocation } from 'react-router-dom'

import { ZariContext } from '_zari/context'

import { Button, Col, Divider, Row, Form } from '_zari/core'
import Yup from '_zari/validation/_yup'

import { PeriodoContext } from 'contexts/PeriodoContext'

import { OrigenesContabilidadConstant } from 'constants/index'

import { IdGen, KeyEventUtil, ZrSwal } from 'utils'

import * as PlantillaGeneralService from 'services/plantillaGeneralContabilidad.service'
import * as AsientosContablesService from 'services/asientosContables.service'

import * as FormGroups from './formGroups'
import * as Sections from './sections'
import { AdminContext } from 'contexts/AdminContext'

const commonReducer = (state, action) => {
  switch (action.type) {
    case 'agregar':
      return [
        ...state,
        {
          ...action.payload,
          _idFila: IdGen.newId()
        }
      ]
    case 'actualizar':
      return state.map(val => {
        if (val._idFila === action.payload._idFila) {
          val = {
            ...val,
            ...action.payload
          }
        }
        return val
      })
    case 'fill':
      return action.payload.map(x => ({
        ...x,
        _idFila: IdGen.newId()
      }))
    case 'eliminar':
      return state.filter(val => val._idFila !== action.payload._idFila)
    default:
      return state
  }
}

export const ValidationSchema = Yup.object().shape({
  fechaDocumento: Yup.string().required(),
  glosa: Yup.string().required()
})

const MainForm = ({ resetMainForm, editData }) => {
  const __zariContext = React.useContext(ZariContext)
  const __adminContext = React.useContext(AdminContext)
  const __periodoContext = React.useContext(PeriodoContext)

  const location = useLocation()

  const [plantillaEncontrada, setPlantillaEncontrada] = React.useState(false)
  const [mostrarPartida, setMostrarPartida] = React.useState(false)
  const [checkedActualizar, setCheckedActualizar] = React.useState(false)
  const [diferenciaSoles, setDiferenciaSoles] = React.useState(0)

  const [detalles, detallesDispatcher] = React.useReducer(commonReducer, editData.detalles, innerDetails => {
    if (innerDetails) {
      return innerDetails.map(x => ({
        ...x,
        _idGlosa: x.glosa || '',
        _idFila: IdGen.newId(),
        modalValido: true
      }))
    } else {
      return []
    }
  })

  const mainFormikRef = React.useRef(null)

  React.useEffect(() => {
    if (editData._id) {
      setPlantillaEncontrada(true)
    }
  }, [editData])


  const formInitialValues = React.useMemo(() => {
    let { origen } = queryString.parse(location.search)

    origen = origen || "10PRE"

    const plantilla = origen !== OrigenesContabilidadConstant.DIARIO || origen !== OrigenesContabilidadConstant.DIARIO  ? 'NO_ID' : ''

    return {
      _id: editData._id || '',
      _idPartida: editData._idPartida || '',
      _idPlantilla: editData._idPlantilla || plantilla,
      fechaDocumento: editData.fechaDocumento || '',
      glosa: editData.glosa || '',
      origen: origen,
      tipoCambio: editData.tipoCambio || '',
      numeroOperacion: editData.numeroOperacion || ''
    }
  }, [editData, location.search])

  const clickContinuar = React.useCallback(
    ({ _idPlantilla, tipoCambio, glosa, _idGlosa }) => {
      if (!tipoCambio || tipoCambio === 1) {
        __zariContext.snack('error', 'Ingresar tipo de cambio.')
        return
      }

      if (_idPlantilla === 'NO_ID') {
        setPlantillaEncontrada(true)
        detallesDispatcher({
          type: 'agregar',
          payload: { tipoCambio, glosa, _idGlosa }
        })
      } else {
        PlantillaGeneralService.getxIdAsDetalles(_idPlantilla).then(({ data }) => {
          if (data) {
            const newData = data.map(x => ({
              ...x,
              moneda: x.cuentaContableMoneda,
              tipoCambio: tipoCambio,
              glosa: glosa,
              _idGlosa: _idGlosa
            }))

            detallesDispatcher({ type: 'fill', payload: newData })
            setPlantillaEncontrada(true)
            KeyEventUtil.leaveOnKeyEvent('input-det-importeDebe-0')
          }
        })
      }
    },
    [__zariContext]
  )

  const onSubmit = React.useCallback(
    (data, { setSubmitting }) => {

      const arrFecha = data.fechaDocumento.split('/')
      const mesDocumento = parseInt(arrFecha[1])
      const mesTrabajo = parseInt(__adminContext.mesTrabajo)
      if (mesTrabajo !== 0) {
        if (mesDocumento !== mesTrabajo) {
          ZrSwal.showAlert({
            tipo: 'error',
            mensaje: 'Fecha de documento es diferente al mes de contabilidad.',
            onSuccess: () => setSubmitting(false)
          })
          return
        }
      }

      const nuevosDetalles = [...detalles].filter(x => x.importeDebe || x.importeHaber)

      for (let i = 0; i < nuevosDetalles.length; i++) {
        const element = nuevosDetalles[i]

        if (!element.importeDebe && !element.importeHaber) {
          __zariContext.snack('error', 'Verificar montos ingresados en los detalles.')
          return
        }

        if (!element.cuentaContable) {
          __zariContext.snack('error', 'Verificar cuentas contables ingresadas.')
          return
        }
      }

      if (diferenciaSoles !== 0) {
        ZrSwal.showAlert({
          tipo: 'warning',
          mensaje: 'Se encontro un descuadre en soles, ¿Desea continuar?',
          showCerrar: true,
          onSuccess: () => {
            const formData = {
              ...data,
              flagActualizarPlantilla: checkedActualizar,
              detalles: nuevosDetalles
            }

            if (!mostrarPartida) {
              formData._idPartida = ''
            } else {
              if (!formData._idPartida) {
                ZrSwal.showAlert({
                  tipo: 'error',
                  mensaje: 'Seleccionar partida.',
                  onSuccess: () => setSubmitting(false)
                })
                return
              }
            }

            AsientosContablesService.nuevoAsientoContable2(formData)
              .then(({ data }) => {
                ZrSwal.showAlert({
                  tipo: 'success',
                  mensaje: (
                    <>
                      <span>Operaciones Generadas :</span> <br />
                      {data.operacionesGeneradas.map((x, k) => (
                        <React.Fragment key={k}>
                          <span>
                            {k + 1}. {x}
                          </span>{' '}
                          <br />
                        </React.Fragment>
                      ))}
                    </>
                  ),
                  onSuccess: () => {
                    resetMainForm()
                  }
                })
              })
              .catch(({ data: { error } }) => {
                __zariContext.snack('error', error)
                setSubmitting(false)
              })
              .finally(() => {
                __periodoContext.actualizarEstados()
              })
          },
          onClose: () => {
            setSubmitting(false)
          }
        })
      } else {
        const formData = {
          ...data,
          flagActualizarPlantilla: checkedActualizar,
          detalles: nuevosDetalles
        }

        if (!mostrarPartida) {
          formData._idPartida = ''
        } else {
          if (!formData._idPartida) {
            ZrSwal.showAlert({
              tipo: 'error',
              mensaje: 'Seleccionar partida.',
              onSuccess: () => setSubmitting(false)
            })
            return
          }
        }

        AsientosContablesService.nuevoAsientoContable2(formData)
          .then(({ data }) => {
            ZrSwal.showAlert({
              tipo: 'success',
              mensaje: (
                <>
                  <span>Operaciones Generadas :</span> <br />
                  {data.operacionesGeneradas.map((x, k) => (
                    <React.Fragment key={k}>
                      <span>
                        {k + 1}. {x}
                      </span>{' '}
                      <br />
                    </React.Fragment>
                  ))}
                </>
              ),
              onSuccess: () => {
                resetMainForm()
              }
            })
          })
          .catch(({ data: { error } }) => {
            __zariContext.snack('error', error)
            setSubmitting(false)
          })
          .finally(() => {
            __periodoContext.actualizarEstados()
          })

      }

      // const parsedDate = DateHandler.parse(data.fechaDocumento)
      // const mesSeleccionado = parsedDate.getMonth() + 1
      // const anioSeleccionado = parsedDate.getFullYear()
      // const mesContabilidad = parseFloat(__adminContext.mesTrabajo)
      // const anioContabilidad = parseFloat(__adminContext.anioTrabajo)

      // const fechaMayorAContabilidad = mesSeleccionado !== mesContabilidad || anioSeleccionado !== anioContabilidad

      // if (fechaMayorAContabilidad) {
      //   ZrSwal.showAlert({
      //     tipo: 'error',
      //     mensaje: 'Fecha de documento distinto al mes de contabilidad.',
      //     onSuccess: () => setSubmitting(false)
      //   })
      //   return
      // }


    },
    [detalles, __zariContext, __periodoContext, mostrarPartida, checkedActualizar, resetMainForm, diferenciaSoles, __adminContext]
  )

  const clickGuardar = React.useCallback(() => {
    mainFormikRef.current.handleSubmit()
  }, [])

  return (
    <>
      <Formik
        initialValues={formInitialValues}
        innerRef={mainFormikRef}
        validationSchema={ValidationSchema}
        onSubmit={onSubmit}
      >
        {({ errors, touched, isSubmitting, values, setFieldValue, setValues }) => (
          <Form>
            <Row>
              {values._id ? (
                <Col md={3}>
                  <FormGroups.NumeroOperacionFormGroup iValue={values.numeroOperacion} />
                </Col>
              ) : (
                <>
                  <Col md={3}>
                    <FormGroups.PlantillaFormGroup
                      fIsSubmitting={isSubmitting || plantillaEncontrada}
                      iError={errors._idPlantilla}
                      iTouched={touched._idPlantilla}
                      vOrigen={values.origen}
                      setFieldValue={setFieldValue}
                    />
                  </Col>
                </>
              )}
              {!values._id && (
                <Col md={3}>
                  <FormGroups.GlosaFormGroup
                    fIsSubmitting={isSubmitting}
                    iError={errors.glosa}
                    iTouched={touched.glosa}
                  />
                </Col>
              )}
              {mostrarPartida && (
                <Col md={3}>
                  <FormGroups.TrabajadorFormGroup
                    fIsSubmitting={isSubmitting}
                    iError={errors._idTrabajador}
                    iTouched={touched._idTrabajador}
                    iValue={values.nombreTrabajador}
                    setValues={setValues}
                    values={values}
                  />
                </Col>
              )}
              <Col md={2}>
                <Form.Checkbox
                  id='checkbox-activarfondofijo'
                  name='checkbox-activarfondofijo'
                  label='Activar Fondo Fijo'
                  checked={mostrarPartida}
                  onChange={() => setMostrarPartida(prev => !prev)}
                />
              </Col>
            </Row>
            <Row>
              <Col md={3}>
                <FormGroups.FechaDocumentoFormGroup
                  fIsSubmitting={isSubmitting}
                  iError={errors.fechaDocumento}
                  iTouched={touched.fechaDocumento}
                />
              </Col>
              {!values._id && (
                <Col md={3}>
                  <FormGroups.TipoCambioFormGroup
                    iError={errors.tipoCambio}
                    iFechaDocumento={values.fechaDocumento}
                    iTouched={touched.tipoCambio}
                    setFieldValue={setFieldValue}
                  />
                </Col>
              )}
              {mostrarPartida && (
                <Col md={3}>
                  <FormGroups.PartidaFormGroup
                    fIsSubmitting={isSubmitting}
                    iError={errors._idPartida}
                    iTouched={touched._idPartida}
                    vSocio={values._idTrabajador}
                  />
                </Col>
              )}
              {!plantillaEncontrada && (
                <>
                  <Col md={2}>
                    <Button id='button-continuar' onClick={() => clickContinuar(values)} block>
                      Continuar
                    </Button>
                  </Col>
                </>
              )}
            </Row>
            {plantillaEncontrada && (
              <>
                <Divider />
                <Sections.DetallesTablaSection
                  detalles={detalles}
                  setDiferenciaSoles={setDiferenciaSoles}
                  dispatch={detallesDispatcher}
                  vOrigen={values.origen}
                  vTipoCambio={values.tipoCambio}
                  vGlosa={values.glosa}
                  vIdGlosa={values._idGlosa}
                  setFieldValue={setFieldValue}
                />
                <Divider />
                <Sections.ResumenMontosSection
                  detalles={detalles}
                  diferenciaSoles={diferenciaSoles}
                  setDiferenciaSoles={setDiferenciaSoles}
                  dispatch={detallesDispatcher}
                  clickGuardar={clickGuardar}
                  vId={values._id}
                  resetMainForm={resetMainForm}
                  checkedCheckbox={checkedActualizar}
                  onChangeCheckbox={() => setCheckedActualizar(prev => !prev)}
                  isSubmitting={isSubmitting}
                />
              </>
            )}
          </Form>
        )}
      </Formik>
    </>
  )
}

export default React.memo(MainForm)
